import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * DSH-09 — THE AUDIT LOG, read side.
 *
 * "Every other screen in this product creates or reads business records. This
 * one records the act of creating and changing them."
 *
 * The write side has existed since the foundation and is untouched by this
 * file: `platform/audit.service.ts` is the log's only writer (BR-DSH-09-05),
 * the table refuses UPDATE and DELETE at a trigger AND at the grant
 * (BR-DSH-09-01/-02), and owner ruling R9's posted-only reporting rule is
 * unaffected because nothing here writes anything. What was missing was a
 * reader: every module has been logging correctly for the whole build and no
 * customer could see a word of it.
 *
 * EVERYTHING IN THIS CONTRACT IS READ-ONLY. There is no create route, no
 * update route and no delete route, and their absence is a requirement rather
 * than an omission — DSH-09 §6 lists Edit, Delete and New as "Does not exist /
 * Nobody / Always", and adds: "Those last three absences are requirements, not
 * omissions."
 */
// ── The eight actions (DSH-09 §5b) ──────────────────────────────────────────
/**
 * "Exactly eight actions exist." A closed set, in the spec's own order.
 *
 * ── WHY THIS IS DERIVED RATHER THAN STORED ─────────────────────────────────
 *
 * `audit_log.event` holds a fine-grained name — `invoice.post`,
 * `leave.reject`, `role.duplicate` — about 180 of them, and the column has
 * carried a `TODO(phase-2): promote to a closed enum` from the foundation
 * until 20260815020000 replaced it with the reasoning below.
 * The obvious reading of DSH-09 is that the eight values ARE that enum, and it
 * is wrong twice over:
 *
 *   the log is APPEND-ONLY and years of rows already exist. Rewriting `event`
 *     to one of eight values would be editing the audit log, which is the one
 *     thing BR-DSH-09-01 forbids absolutely — "by anybody, including the Owner
 *     and including the system itself";
 *
 *   and the eight would LOSE information the log already has. "Update" cannot
 *     tell a posted invoice from a renamed warehouse; `invoice.post` can. The
 *     spec's own §5i sample rows show the finer名 detail in the change lines,
 *     so the eight are a VOCABULARY FOR THE ACTION CHIP, not a replacement for
 *     the event.
 *
 * So the eight are a CLASSIFICATION of the stored event, computed on read by
 * the tables below. One definition, in the contracts package, rendered twice:
 * as a function for the screen and as SQL for the filter, from the same data —
 * which is the only way the chip a user sees and the filter they pick can be
 * guaranteed to agree.
 */
export const AUDIT_ACTIONS = [
    'create',
    'update',
    'delete',
    'approve',
    'reject',
    'login',
    'export',
    'permission_change',
];
export const AuditActionSchema = z.enum(AUDIT_ACTIONS);
/**
 * BR-DSH-09-17 — "The Security-sensitive view must show EXACTLY the Permission
 * Change, Delete, Login and Export actions." Four, and the spec explains why
 * these four: they are "the four things that indicate someone is doing
 * something they should not: changing permissions, deleting records, signing
 * in, and taking data out."
 */
export const SECURITY_SENSITIVE_ACTIONS = [
    'permission_change',
    'delete',
    'login',
    'export',
];
/** The three tabs of §5d. */
export const AUDIT_VIEWS = ['global', 'user', 'security'];
export const AuditViewSchema = z.enum(AUDIT_VIEWS);
/**
 * Events whose action cannot be read off the verb, because the ENTITY decides.
 * Every one of these is a judgement, so every one is written down:
 *
 *  - anything that touches a ROLE is a Permission Change, not a Create or an
 *    Update. DSH-09 §5b: Permission Change "is the one action that changes
 *    what everybody else is allowed to do", and it is given its own colour for
 *    exactly that reason. `role.create` classified as Create would drop the
 *    most consequential rows in the log out of the Security-sensitive view.
 *  - the OWNER FLAG moving is the same class of event (R18: it is the one
 *    grant nobody can be given by a role).
 *  - `permission.denied` — a REFUSAL. The eight-value vocabulary has no
 *    "refused" member and BR-DSH-09-17 fixes the security view at four, so a
 *    refusal has to land in one of them or become invisible to the
 *    administrator who most needs it. Permission Change is where it belongs:
 *    it is the permission system speaking, the row's own `outcome` column
 *    already says `denied`, and the alternative — leaving it in the Update
 *    fallback — would bury attempted access under ordinary business edits.
 *  - the attendance corrections, whose verbs are compounds the verb table
 *    would miss.
 *  - the platform-operator rows: an operator EXPORTING a tenant's data is an
 *    Export whatever its event name says (BR-DSH-09-21's spirit).
 */
export const AUDIT_ACTION_BY_EVENT = {
    'role.create': 'permission_change',
    'role.update': 'permission_change',
    'role.duplicate': 'permission_change',
    'role.deactivate': 'permission_change',
    'role.reactivate': 'permission_change',
    'user.role_assign': 'permission_change',
    'owner.transfer_in': 'permission_change',
    'owner.transfer_out': 'permission_change',
    'owner.transfer_started_by_platform': 'permission_change',
    'permission.denied': 'permission_change',
    'attendance.correction_approve': 'approve',
    'attendance.correction_reject': 'reject',
    'attendance.correction_request': 'create',
    'tenant.exported_by_platform': 'export',
};
/**
 * Otherwise the VERB — the part after the dot — decides. Only the verbs that
 * mean something other than "was changed" need listing; everything else falls
 * to Update, which is what DSH-09 calls "An existing record was changed".
 *
 * `post`, `confirm` and `complete` are Approve rather than Update because in
 * this product posting IS the approval: `purchasing.approve` is the permission
 * that gates posting a bill, and a log whose Approve chip did not cover it
 * would disagree with the permission grid it is meant to audit.
 */
export const AUDIT_ACTION_BY_VERB = {
    create: 'create',
    signup: 'create',
    invite: 'create',
    request: 'create',
    add: 'create',
    delete: 'delete',
    approve: 'approve',
    approval: 'approve',
    post: 'approve',
    confirm: 'approve',
    complete: 'approve',
    reject: 'reject',
    signin: 'login',
    signout: 'login',
    export: 'export',
};
/** DSH-09's "Update — an existing record was changed": the residue. */
export const AUDIT_ACTION_FALLBACK = 'update';
/** One event name → one of the eight. Total, by construction. */
export function auditActionFor(event) {
    const byEvent = AUDIT_ACTION_BY_EVENT[event];
    if (byEvent)
        return byEvent;
    const verb = event.slice(event.indexOf('.') + 1);
    return AUDIT_ACTION_BY_VERB[verb] ?? AUDIT_ACTION_FALLBACK;
}
// ── The entity types the doors write ────────────────────────────────────────
/**
 * EVERY `target_type` A DOOR IN THIS BUILD WRITES TODAY — the ROSTER.
 *
 * ── WHY THIS IS A LIST AND NOT A COLUMN CONSTRAINT ─────────────────────────
 *
 * `audit_log.target_type` stays `text` and the wire schema stays
 * `z.string().nullable()`, for the same two reasons the `event` column gives
 * one row above it in `packages/db/src/schema/audit-log.ts`: a database enum
 * would make every new module's first event a migration, and the table is
 * APPEND-ONLY, so years of existing rows carry whatever their writer said at
 * the time. A type this list does not know is therefore a NORMAL state — a row
 * written by a door that has since been renamed or removed — and not a fault.
 * The closure lives HERE, over the writers, where a name can be added by
 * editing a list rather than by rewriting history.
 *
 * ── WHAT THE LIST IS FOR ───────────────────────────────────────────────────
 *
 * DSH-09's entity link. `apps/web/src/features/audit/audit-entity.ts` gives
 * every member of this list exactly one destination — a path, or a PINNED
 * NULL saying "named, deliberately not linked" with the reason — and a type
 * that is NOT a member renders as plain text. So the list is the boundary
 * between "we know where this opens" and "we do not", and neither side can
 * produce a dead link.
 *
 * ── HOW IT WAS BUILT, AND WHY THE OBVIOUS METHOD IS SHORT BY TWO ───────────
 *
 * By enumerating the WRITERS, not the stored rows. A grep for
 * `targetType: '…'` across `apps/api/src` and `packages/ledger/src` finds 63
 * names; `candidate` and `requisition` are written through
 * `recruitment.service.ts`'s private `auditRow(tx, event, targetType, …)`
 * helper, which passes the type POSITIONALLY and so matches no such grep.
 * That is the same shape that hid 121 of the 278 audit EVENT names when the
 * catalogue in `audit-events.ts` was seeded — see the header of
 * `apps/api/test/audit-events.spec.ts`. `apps/api/test/audit-entity-types.spec.ts`
 * reads both shapes and fails on a door writing a name that is not here.
 *
 * A name written once in development that no door still writes gets a NOTE
 * rather than a seat. There is one, and it is on the i18n side rather than
 * here: `audit.entity.leave_request` exists in both dictionaries and no door
 * writes `leave_request` — HR-04 logs a leave request against the EMPLOYEE
 * (`leave.service.ts:339/449/483`), so the key is an orphan, kept because
 * deleting somebody's finished translation work is the expensive direction.
 */
export const AUDIT_ENTITY_TYPES = [
    'account',
    'accounting_period',
    'api_key',
    'asset',
    'asset_category',
    'audit_log',
    'bank_account',
    'bill',
    'bom_version',
    'candidate',
    'cash_transfer',
    'check',
    'consumption_voucher',
    'credit_note',
    'currency',
    'data_transfer',
    'delivery_note',
    'depreciation_run',
    'einvoice_submission',
    'email',
    'employee',
    'goods_receipt',
    'import_order',
    'invoice',
    'item',
    'item_category',
    'job_role',
    'journal_entry',
    'lead',
    'leave_type',
    'legal_entity',
    'manufacturer',
    'mrp_run',
    'notification',
    'number_sequence',
    'opp_stage',
    'opportunity',
    'partner',
    'partner_group',
    'payment',
    'payment_term',
    'payroll_run',
    'price_tier',
    'project',
    'purchase_order',
    'purchase_return',
    'quotation',
    'receipt',
    'reconciliation',
    'requisition',
    'revaluation',
    'role',
    'sales_order',
    'setting',
    'stock_adjustment',
    'stock_count',
    'stock_movement',
    'stock_transfer',
    'task',
    'tenant',
    'timesheet_entry',
    'timesheet_week',
    'unit',
    'unit_group',
    'user',
    'warehouse',
    'work_order',
];
const AUDIT_ENTITY_TYPE_SET = new Set(AUDIT_ENTITY_TYPES);
/**
 * Is this stored string a type the roster knows?
 *
 * A PREDICATE over `string`, deliberately, and not a schema: the value arrives
 * from an append-only table and a row from a retired door is history rather
 * than a validation failure. Narrowing is what the caller wants — the type
 * either has a destination or renders as text.
 */
export function isAuditEntityType(value) {
    return AUDIT_ENTITY_TYPE_SET.has(value);
}
// ── The rows ────────────────────────────────────────────────────────────────
/**
 * One change line: `{field, old value, new value}` (§5g). The stored `changes`
 * JSON is written by ~180 call sites in several shapes, so the reader
 * normalises rather than demanding the writers change — a shape migration on
 * an append-only table is not possible, which is the whole point of it.
 */
export const AuditChangeSchema = z
    .object({
    field: z.string(),
    /** Em dash on screen when null (§5g: "An empty value… renders as an em dash"). */
    from: z.string().nullable(),
    to: z.string().nullable(),
})
    .strict();
export const AuditEntrySchema = z
    .object({
    /**
     * DSH-09 field 1 asks for `AL-######`, "never reused". This is the row's
     * uuidv7 primary key instead — unique and never reused (BR-DSH-09-20,
     * "including after a system restore", which a counter cannot promise and a
     * uuidv7 can), and time-ordered so it sorts the way the screen reads.
     * DIVERGENCE, recorded: the shape is not the spec's, the guarantee is
     * stronger, and inventing an `AL-` counter would mean writing to an
     * append-only table that already holds every row.
     */
    id: z.uuid(),
    /** BR-DSH-09-12: stored UTC, shown in the reader's local time. */
    occurredAt: z.iso.datetime(),
    /** One of the eight (§5b) — derived, see auditActionFor. */
    action: AuditActionSchema,
    /**
     * The stored fine-grained event, kept alongside the chip. It is what makes
     * the row worth reading a year later: "Update / Invoice" is a fact,
     * `invoice.post` is the answer.
     */
    event: z.string(),
    /** Field 3. Null when the actor's row is gone — the email survives it. */
    actorId: z.uuid().nullable(),
    actorName: z.string().nullable(),
    actorEmail: z.string().nullable(),
    actorType: z.enum(['user', 'system', 'job', 'vendor_staff', 'anonymous']),
    /**
     * Fields 5 and 6. Null for an event with no record (a sign-in).
     *
     * A STRING and not `z.enum(AUDIT_ENTITY_TYPES)`. The table is append-only,
     * so a row written by a door that has since been renamed would fail to
     * PARSE under an enum — the reader would lose the entry entirely, which is
     * the one outcome an audit log may not have. The roster narrows it on
     * arrival instead (`isAuditEntityType`), and a name outside the roster is
     * shown as text.
     */
    entityType: z.string().nullable(),
    entityId: z.uuid().nullable(),
    /** Field 7 — zero or more; empty for actions that change no field. */
    changes: AuditChangeSchema.array(),
    /**
     * Not on the spec's nine, and load-bearing: a `denied` row is the reason
     * the Security-sensitive view exists, and a row that does not say whether
     * it SUCCEEDED is worse than useless in an investigation.
     */
    outcome: z.enum(['success', 'failure', 'denied']),
    reason: z.string().nullable(),
    /**
     * Field 8. §5a: fields 8–10 are "stored but not shown in the table", with
     * a proposed default of a detail panel and the CSV. Adopted: the row
     * carries it, the table does not draw it, the detail panel does.
     */
    ip: z.string().nullable(),
})
    .strict();
// ── The query ───────────────────────────────────────────────────────────────
/**
 * BR-DSH-09-18 — "The table must page 8 entries at a time, newest first, and
 * must state the range and the total."
 *
 * Eight is small, and the spec knows it: "Paging is real here and exists
 * nowhere else in PART 14… The audit log pages, because an investigator has to
 * be able to reach every entry, not just the newest eight."
 */
export const AUDIT_PAGE_SIZE = 8;
/**
 * The ceiling, for the export path. Not a page size — a refusal to stream an
 * unbounded result set into one response. The CSV honours the current filters
 * (DSH-09 §6, "Downloads the currently filtered entries"), and a filter that
 * still selects more than this is one the investigator needs to narrow.
 */
export const AUDIT_EXPORT_MAX = 5000;
export const AuditQuerySchema = z
    .object({
    /** §5d. `user` additionally requires `userId`. */
    view: AuditViewSchema.default('global'),
    /**
     * BR-DSH-09-16 — "The search box must match both the entity id and the
     * text of the changes", so searching `Karim` finds the row that set a new
     * employee's name.
     */
    search: z.string().trim().max(200).optional(),
    userId: z.uuid().optional(),
    action: AuditActionSchema.optional(),
    /** BR-DSH-09-15: the screen only ever offers types that appear in the log. */
    entityType: z.string().trim().max(60).optional(),
    /** §5e fields 15/16 — compared against the DATE PART of the timestamp. */
    from: IsoDateSchema.optional(),
    to: IsoDateSchema.optional(),
    /** 1-based. BR-DSH-09-19 makes the screen reset this on any filter change. */
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(AUDIT_EXPORT_MAX).default(AUDIT_PAGE_SIZE),
})
    .strict();
export const AuditPageSchema = z
    .object({
    entries: AuditEntrySchema.array(),
    /** Field 17's "of 12" — the total AFTER filtering. */
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    /**
     * BR-DSH-09-15 — the entity types that ACTUALLY APPEAR in this tenant's
     * log, "so the list never offers a choice that returns nothing". Computed
     * over the whole tenant rather than the current page, or the dropdown
     * would shrink as you paged through it.
     */
    entityTypes: z.string().array(),
    /** §5e field 12's "All users, then every user" — the actors in the log. */
    users: z
        .object({ id: z.uuid(), name: z.string() })
        .strict()
        .array(),
})
    .strict();
export const AuditExportResponseSchema = z
    .object({
    /** CSV text, UTF-8 with a BOM added by the caller's download helper. */
    csv: z.string(),
    /** §6: the button "confirms how many were exported". */
    exported: z.number().int().min(0),
})
    .strict();
export const AUDIT_MESSAGES = {
    /** The immutability banner (§1) — the screen's most important sentence. */
    immutable: 'Append-only & immutable — audit entries can never be edited or deleted, by any role, and not by the Owner.',
    /** A `view=user` query with no user chosen. */
    userRequired: 'Choose a person to see their activity.',
    /** AUDIT_EXPORT_MAX exceeded. Names the number so the advice is actionable. */
    tooManyToExport: (max) => `That is more than ${max} entries. Narrow the dates or the filters and try again.`,
};
/**
 * D46 — the audit-export ceiling refusal, structured. `max` is a genuine count
 * but the noun "entries" is fixed plural, so it interpolates raw. Sentence
 * parity pinned by `refusal-census.spec.ts`.
 */
export const AUDIT_REFUSALS = {
    tooManyToExport: (max) => ({
        message: AUDIT_MESSAGES.tooManyToExport(max),
        key: 'audit.errTooManyToExport',
        params: { max },
    }),
};
export const auditContract = c.router({
    list: {
        method: 'GET',
        path: '/api/audit',
        query: AuditQuerySchema,
        responses: { 200: AuditPageSchema, 400: MessageSchema, 403: MessageSchema },
        summary: 'DSH-09: the audit log, filtered and paged. Read-only; gated by admin.read_audit_log (BR-DSH-09-13).',
    },
    /**
     * POST, for a read — deliberately. BR-DSH-09-21: "Exporting from the audit
     * log must itself be logged as an Export action", and a GET that writes a
     * row would be a GET with a side effect, which every cache, prefetcher and
     * link-preview in the world is entitled to fire on its own.
     */
    exportCsv: {
        method: 'POST',
        path: '/api/audit/export',
        body: AuditQuerySchema.omit({ page: true, pageSize: true }),
        responses: {
            200: AuditExportResponseSchema,
            400: MessageSchema,
            403: MessageSchema,
            413: MessageSchema,
        },
        summary: 'DSH-09 §6: the currently filtered entries as CSV. Writes its own audit row (BR-DSH-09-21).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=audit.js.map